import { useEffect, useRef } from 'react';

/**
 * useFocusTrap — WAI-ARIA APG dialog focus trap without adding a
 * runtime dependency.
 *
 * Usage:
 *   const ref = useFocusTrap({ active, onClose });
 *   return active ? <div ref={ref} role="dialog" aria-modal="true">…</div> : null;
 *
 * Behavior, per
 * https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/:
 *   1. On activation, focus the first tabbable element inside the
 *      container (or the container itself if empty).
 *   2. Tab from the last tabbable wraps to the first; Shift+Tab from
 *      the first wraps to the last — focus never escapes.
 *   3. Escape calls `onClose()` (optional) so keyboard users can dismiss.
 *   4. On deactivation, focus returns to the element that had focus
 *      when the dialog mounted — keeping the tab order sensible for
 *      screen-reader users who rewind the page.
 *
 * Review item M3.
 */

// Tabbable-selector: matches the APG pattern's list, excludes disabled.
const TABBABLE = [
  'a[href]',
  'area[href]',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'button:not([disabled])',
  'iframe',
  'object',
  'embed',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',');

function isVisible(n) {
  // In a real browser, offsetParent is null for display:none and
  // detached elements. In jsdom there's no layout at all, so offsetParent
  // is always null — we can't use it there. Fall back to attribute
  // signals (hidden / aria-hidden / tabindex=-1) which both environments
  // agree on.
  if (n.hasAttribute('hidden')) return false;
  if (n.getAttribute('aria-hidden') === 'true') return false;
  if (n.getAttribute('tabindex') === '-1') return false;
  return true;
}

function getTabbable(container) {
  if (!container) return [];
  const nodes = Array.from(container.querySelectorAll(TABBABLE));
  return nodes.filter((n) => !n.hasAttribute('disabled') && isVisible(n));
}

export function useFocusTrap({ active, onClose } = {}) {
  const ref = useRef(null);
  // Keep the latest onClose in a ref so the trap effect can depend only on
  // [active]. A fresh inline `onClose` on every parent render would otherwise
  // tear down and re-arm the trap each render — flickering focus (trigger ↔
  // dialog) and thrashing the keydown listener. (Review: modal-flicker fix.)
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!active) return undefined;
    const container = ref.current;
    if (!container) return undefined;

    const previouslyFocused = typeof document !== 'undefined' ? document.activeElement : null;

    // Initial focus: first tabbable, else the container itself.
    const tabbables = getTabbable(container);
    const first = tabbables[0];
    if (first) {
      first.focus();
    } else {
      // Give the container a temporary tabindex so keyboard users land on it.
      if (!container.hasAttribute('tabindex')) {
        container.setAttribute('tabindex', '-1');
      }
      container.focus();
    }

    function onKeyDown(e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        const onClose = onCloseRef.current;
        if (typeof onClose === 'function') {
          e.preventDefault();
          onClose();
        }
        return;
      }
      if (e.key !== 'Tab') return;
      const cur = getTabbable(container);
      if (cur.length === 0) {
        e.preventDefault();
        return;
      }
      const firstEl = cur[0];
      const lastEl = cur[cur.length - 1];
      const activeEl = document.activeElement;

      if (e.shiftKey) {
        if (activeEl === firstEl || !container.contains(activeEl)) {
          e.preventDefault();
          lastEl.focus();
        }
      } else {
        if (activeEl === lastEl || !container.contains(activeEl)) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    }

    document.addEventListener('keydown', onKeyDown, true);

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        try { previouslyFocused.focus(); } catch (_) { /* ignore */ }
      }
    };
  }, [active]);

  return ref;
}

export default useFocusTrap;
